/**
 * Validators custom liés au plan d'abonnement (entreprise)
 */

import { useAuthStore } from "@core/stores/auth.store"
import type { CustomValidator, ValidatorWithContext } from "./registry"

type AuthStore = ReturnType<typeof useAuthStore>

const PLANS_EM_EA = ["EM", "EA"] as const

function isAllowedPlanToken(s: string): s is (typeof PLANS_EM_EA)[number] {
    return (PLANS_EM_EA as readonly string[]).includes(s)
}

/**
 * Interprète `condition.value` : absent → mode défaut ; sinon liste de plans autorisés.
 */
function resolveExpectedPlans(value: unknown): "default" | string[] {
    if (value === undefined || value === null || value === "") {
        return "default"
    }
    if (Array.isArray(value)) {
        const list = value.filter(
            (v): v is string => typeof v === "string" && isAllowedPlanToken(v)
        )
        return list
    }
    if (typeof value === "string" && isAllowedPlanToken(value)) {
        return [value]
    }
    return []
}

/**
 * Vérifie le plan d’abonnement (EM / EA).
 *
 * - Sans `value` : même règle que `auth.isSubscriptionActive` (plan EM ou EA, pas « autre »).
 * - Avec `value` : `"EM"` ou `"EA"` (ou tableau de ces codes) — le plan courant doit être dans la liste.
 *
 * Contexte attendu : `{ auth }` depuis `handleUserCondition` (`origin: "user"`).
 *
 * @example Tous les abonnés EM ou EA (équivalent isSubscriptionActive)
 * ```json
 * {
 *   "operator": "custom",
 *   "validatorId": "subscription_plan_em_ea",
 *   "origin": "user"
 * }
 * ```
 *
 * @example Uniquement Energy Audit
 * ```json
 * {
 *   "operator": "custom",
 *   "origin": "user",
 *   "validatorId": "subscription_plan_em_ea",
 *   "value": "EA"
 * }
 * ```
 */
const subscriptionPlanEmEaValidator: CustomValidator = (
    value: unknown,
    context: { auth?: AuthStore }
): boolean => {
    const auth = context?.auth ?? useAuthStore()
    const expected = resolveExpectedPlans(value)
    if (expected === "default") {
        return auth.isSubscriptionActive
    }
    if (expected.length === 0) {
        return false
    }
    // cf. DEV-1327 : on lit le plan dérivé, pas le champ dénormalisé figeable.
    const current = auth.subscriptionPlan
    return expected.includes(current)
}

/**
 * Validator enregistré sous l'id `subscription_plan_em_ea`
 */
export const subscriptionPlanEmEa: ValidatorWithContext = {
    validator: subscriptionPlanEmEaValidator,
}
